import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { LocationRes, TaxonomyRes } from '../../typescript/pages';
import { getLocationDetail } from '../../helper';
import { setPageCacheHeaders } from '../../helper/cache-control';
import { PageTitleHeader, FullWidthMedia as FullWidthMediaProp } from '../../typescript/component';
import PageTitle from '../../components/ui/page-title';
import FullWidthMedia from '../../components/media/full-width-media';
import WistiaVideo from '../../components/media/wistia-video';
import { translate } from '../../helper/translate';
import { certFiles, languageLabel } from '../../helper/language-label';
import styles from '../../styles/Location.module.css';
import * as cheerio from 'cheerio';
import { SEO } from '../../helper/seo';
import { useEffect, useRef, useState } from 'react';
import { mapStyle } from '../../typescript/mapStyle';
import { getMapIcon, getGoogleMapsLoader } from '../../sdk/utils';
import { normLng, parseCoord } from '../../helper/mapHelper';
import Button from '../../components/ui/button';
import { getTaxonomy } from '../../sdk/entry';
import { getEditable } from '../../helper';

// Live Preview imports
import ContentstackLivePreview from '@contentstack/live-preview-utils';
import { initLivePreview, initializeContentStackNewSdk } from '../../contentstack-sdk/utils';
import { getCachedAsset } from '../../helper/utils';

type Props = {
  location: LocationRes;
  decodedPhoneHTML?: string | null;
  locale: string;
  metalsTaxonomy?: TaxonomyRes[];
};

// Utility functions
const toProperCase = (str: string) =>
  str
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const isPhoneNumber = (text: string): boolean => {
  const phoneRegex = /^(\+1\s?)?(\d{3})[-.\s]?(\d{3})[-.\s]?(\d{4})$/;
  return phoneRegex.test(text.trim());
};

const formatLocation = (location: LocationRes) => {
  return `${location.city || ''}${
    location.city && location.state ? ', ' : ''
  }${location.state || ''} ${location.postal_code || ''}`.trim();
};

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const rawSlug = Array.isArray(context.params?.slug)
    ? context.params.slug.join('/')
    : context.params?.slug || '';
  const entryUrl = `/locations/${rawSlug}`;
  const locale = context.locale || context.defaultLocale || 'en-us';

  try {
    const location = await getLocationDetail({
      entryUrl: entryUrl,
      locale: locale
    });
    if (!location) return { notFound: true };
    // return { notFound: true };

    // Fetch metals taxonomy terms to get their full names
    const metalsTaxonomy = await getTaxonomy('metals').catch(() => []);

    const decodeRichText = (text: string): string => {
      const $ = cheerio.load(text);
      return $('body').html() || '';
    };

    const phoneIsRich =
      location.phone &&
      !/^(\+1\s?)?(\d{3})[-.\s]?(\d{3})[-.\s]?(\d{4})$/.test(location.phone.trim());
    const decodedPhoneHTML = phoneIsRich ? decodeRichText(location.phone) : null;

    setPageCacheHeaders(context.res);

    return {
      props: {
        location,
        decodedPhoneHTML,
        locale,
        metalsTaxonomy,
      },
    };
  } catch (err) {
    return { notFound: true };
  }
};

export default function LocationPage({
  location,
  decodedPhoneHTML,
  locale,
  metalsTaxonomy = [],
}: Props) {
  const [isPreview, setPreview] = useState(false);
  const [data, setData] = useState(location);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const formattedLocation = formatLocation(data);

  const pageTitleData: PageTitleHeader = {
    title: data.header_title,
    description: data.header_description,
  };

  const fullWidthMedia: FullWidthMediaProp = {
    media: data.map_media,
    wistia: '',
  };

  const getContent = async ({ stack }: {stack: any}) => {
    const page = await getLocationDetail({
      entryUrl: window.location.pathname,
      locale: locale,
      stackInstance: stack,
      clientSide: true
    });

    // Store data into state so we can dynamically change update the fields
    setData(page);
  };

  useEffect(() => {
    setData(location);
  }, [location]);

  useEffect(() => {
    const stack = initializeContentStackNewSdk({ preview: true });

    initLivePreview({stackInstance: stack});
    ContentstackLivePreview.onEntryChange(() => {
      
      // It's loaded from the live preview so we want to finish up the setup
      if (stack.config.live_preview && stack.config.live_preview.live_preview) {
        setPreview(true);
        getContent({stack});
      }
    });
  }, []);

  // Initialize Google Maps, rebuilding if the locale (map language) changes.
  useEffect(() => {
    let cancelled = false;
    const loader = getGoogleMapsLoader('AIzaSyAH8sRFq8E04g-lGKsvFvUCczd5n7pDrBA', locale);

    if (!mapRef.current || !data) return;

    const initMap = async () => {
      const { Map } = (await loader.importLibrary('maps')) as google.maps.MapsLibrary;
      const { Marker } = (await loader.importLibrary('marker')) as google.maps.MarkerLibrary;

      if (cancelled || !mapRef.current) return;

      if (data?.latitude && data?.longitude) {
        const map = new Map(mapRef.current!, {
          center: {
            lat: parseCoord(data.latitude)!,
            lng: normLng(parseCoord(data.longitude)!),
          },
          zoom: 9,
          disableDefaultUI: false,
          gestureHandling: 'none',
          keyboardShortcuts: true,
          draggable: true,
          scrollwheel: true,
          disableDoubleClickZoom: false,
          styles: mapStyle,
        });

        map.addListener('mouseover', () => map.setOptions({ gestureHandling: 'greedy' }));
        map.addListener('mouseout', () => map.setOptions({ gestureHandling: 'none' }));

        new Marker({
          position: {
            lat: parseCoord(data.latitude)!,
            lng: normLng(parseCoord(data.longitude)!),
          },
          map,
          title: data.header_title || 'Location',
          icon: {
            url: getMapIcon(),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 32),
          },
        });
      }
    };

    initMap();

    return () => {
      cancelled = true;
    };
  }, [data, locale]);

  // Render certification buttons: one per file, so a cert with several languages lists them together
  const renderCertifications = () => {
    if (!data.certifications?.length) return null;

    return (
      <>
        <p className={styles.taxonomy}>{translate('certifications', locale)}:</p>
        {data.certifications.flatMap((cert, idx) => {
          const title = cert.certification?.[0]?.title || cert.title;
          if (!title) return [];

          const files = certFiles(cert);
          return (files.length ? files : [{ link: undefined, languages: undefined }]).map((file, i) => (
            <Button
              key={`${idx}-${i}`}
              text={[title, languageLabel(file.languages, locale)].filter(Boolean).join(' ')}
              link={file.link}
              propWidth="fit-content"
              propHeight="1.75rem"
              propPadding="0.5rem 1rem"
              propFontSize="0.75rem"
              propBackgroundColor="var(--primary-500)"
              propHoverBackgroundColor="white"
              propTextColor="white"
              propHoverText="var(--primary-500)"
              propBorderColor="var(--primary-500)"
              propMargin="0 1rem 1rem 0"
              showDownloadIcon={true}
            />
          ));
        })}
      </>
    );
  };

  // Render metals taxonomy
  const renderMetals = () => {
    const metals = data.taxonomies?.filter(t => t.taxonomy_uid === 'metals');
    if (!metals?.length) return null;

    // Get current brand to determine URL structure
    const currentBrand = process.env.NEXT_PUBLIC_CONTENTSTACK_BRAND || 'dynacast';

    // Create a lookup map from term_uid to taxonomy term name
    // Try matching by uid first, then by extracting the base name from term_uid
    const metalsLookup = new Map<string, string>();
    metalsTaxonomy.forEach(term => {
      metalsLookup.set(term.uid, term.name);
      // Also try matching by the base name (e.g., if term_uid is "300_uid_123", match "300")
      const baseName = term.uid.split('_')[0];
      if (baseName) {
        metalsLookup.set(baseName, term.name);
      }
    });

    // Mapping from simple metal names to brand-specific URL structure
    const metalUrlMapping: Record<string, string> = {
      '300': '300-series-stainless-steels',
      '400': '400-series-stainless-steels',
      carbon: 'carbon-and-low-alloy-steels',
      cobalt: 'cobalt-based-alloys',
      nickel: 'nickel',
      precipitation: 'precipitation-hardened-alloys',
      thermal: 'thermal-expansion-alloys',
      titanium: 'titanium',
      tool: 'tool-steels',
      aluminum: 'aluminum',
      low: 'low-alloy-steels',
      stainless: 'stainless-steels',
      special: 'special-and-custom-materials'
    };

    // Determine URL path based on brand
    const urlPath = (currentBrand === 'signicast' || currentBrand === 'optimim') ? 'materials' : 'metals';

    return (
      <>
        <p className={styles.taxonomy}>{translate('metals', locale)}:</p>
        {metals.map((metal, idx) => {
          // Extract base name from term_uid for lookup (e.g., "300_uid_123" -> "300")
          const metalNameFromUid = metal.term_uid.split('_')[0];

          // Get the full name from taxonomy lookup (try full term_uid first, then base name)
          const displayText =
            metalsLookup.get(metal.term_uid) ||
            metalsLookup.get(metalNameFromUid) ||
            toProperCase(translate(metalNameFromUid, locale));

          // Use the base name for URL mapping
          const englishMetalName = metalNameFromUid.toLowerCase();
          // Map to brand-specific URL structure, fallback to original name if not found
          const mappedUrl = metalUrlMapping[englishMetalName] || englishMetalName;

          return (
            <Button
              key={idx}
              text={displayText}
              link={`/${urlPath}/${mappedUrl}`}
              propWidth="fit-content"
              propHeight="1.75rem"
              propPadding="0.5rem 1rem"
              propFontSize="0.8rem"
              propBackgroundColor="var(--primary-500)"
              propHoverBackgroundColor="white"
              propTextColor="white"
              propHoverText="var(--primary-500)"
              propBorderColor="var(--primary-500)"
              propMargin="0 1rem 1rem 0"
              showIcon={true}
            />
          );
        })}
      </>
    );
  };

  return (
    <>
      <SEO
        meta_title={data?.seo?.meta_title || 'Default Title'}
        meta_description={data?.seo?.meta_description || 'Default description'}
        keywords={data?.seo?.keywords || 'default, keywords'}
        share_image={''}
        contentType="Location"
        currentLocale={locale}
        baseUrl={process.env.NEXT_PUBLIC_HOSTED_URL || ''}
        authorName={'Form Technologies'}
        streetAddress={data.address || ''}
        city={data.city || ''}
        latitude={data.latitude || ''}
        longitude={data.longitude || ''}
        postalCode={data.postal_code || ''}
        telephone={data.phone || ''}
      />

      <div className={styles.outerWrapper}>
        <PageTitle key="contactPageTitle" page_title={pageTitleData} />

        <div className={styles.moduleWrapper}>
          {/* Media and Map Section */}
          <section className={styles.mediaWrapper}>
            <FullWidthMedia key="fullWidthMedia" full_width_media={fullWidthMedia} />
            <div ref={mapRef} style={{ width: '100%', height: '20rem', borderRadius: '8px' }}></div>
          </section>

          {/* Contact Information */}
          <section className={styles.contactWrapper}>
            <div className={styles.leftPanel}>
              <address>
                <div className={styles.boldText}>{translate('address', locale)}</div>
                {data.address && <p className={styles.normalText}>{data.address}</p>}
                {data.address_2 && <p className={styles.normalText}>{data.address_2}</p>}
                <p className={styles.normalText}>{formattedLocation}</p>
              </address>

              <div className={styles.contactInfo}>
                {data.phone && (
                  <>
                    <p className={styles.largeText}>{translate('phone', locale)}</p>
                    <div className={styles.normalText}>
                      {isPhoneNumber(data.phone) ? (
                        <a href={`tel:${data.phone.replace(/[-.\s]/g, '')}`}>
                          {data.phone}
                        </a>
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: decodedPhoneHTML || '',
                          }}
                        />
                      )}
                    </div>
                  </>
                )}
                {data.sales && (
                  <>
                    <p className={styles.largeText}>Sales</p>
                    <p className={styles.normalText}>
                      <a href={`tel:${data.sales}`}>{data.sales}</a>
                    </p>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Main Content and Taxonomies */}
          <div className={styles.root}>
            <section className={styles.virtualTour}>
              {data.media && (
                <img
                  src={getCachedAsset(data.media.url)}
                  alt={data.media.description}
                  className={styles.image}
                />
              )}
              {data.content && (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: data.content }}
                />
              )}
              <div className={styles.wistia}>
                {data.wistia && <WistiaVideo embedUrl={data.wistia} responsive={true} />}
              </div>
            </section>

            <div className={styles.divider} />

            <div className={styles.taxonomyWrapper}>
              {renderCertifications()}
              {renderMetals()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
